import React from "react";
import { PHOTO } from "../lib/i18n";

export default function Hero({ t }) {
  const scrollToChat = () => {
    document.getElementById("chat")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToLead = () => {
    document.getElementById("lead")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-rule"
    >
      {/* Decorative ASCII-ish backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] font-mono text-[10px] leading-[14px] text-ink select-none"
      >
        <pre className="px-6 md:px-10 lg:px-16 pt-20 whitespace-pre-wrap break-words">
{`> initialising_digital_twin :: nickolass_jensen.v1
> domain :: seo · geo · 3la · ai · woocommerce · sprint
> location :: jevnaker, no  ·  uptime :: 24/7
> hat :: TRUE  ·  hatch :: open  ·  caffeine :: high`}
        </pre>
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 pt-16 md:pt-24 pb-16 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left — editorial type + photo */}
        <div className="lg:col-span-7 animate-fade-up">
          <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.22em] uppercase text-ember">
            <span className="inline-block h-2 w-2 rounded-full bg-terracotta animate-pulse" />
            {t.hero.kicker}
          </div>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tight"
          >
            {t.hero.title_1}{" "}
            <span className="relative inline-block">
              <span className="italic font-medium">{t.hero.title_2}</span>
              <span className="absolute -bottom-1 left-0 right-0 h-[6px] bg-terracotta/80" aria-hidden />
            </span>
          </h1>

          <p className="mt-8 max-w-xl text-base md:text-lg text-mist leading-relaxed">
            {t.hero.lede}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-chat"
              onClick={scrollToChat}
              className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-ink text-paper font-mono text-[12px] tracking-[0.18em] uppercase hover:bg-ember transition-colors"
            >
              <span>{t.hero.cta_chat}</span>
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </button>

            <button
              data-testid="hero-cta-real"
              onClick={scrollToLead}
              className="font-mono text-[12px] tracking-[0.18em] uppercase text-ink underline underline-offset-[6px] decoration-rule hover:decoration-terracotta hover:text-ember transition-colors"
            >
              {t.hero.cta_real}
            </button>
          </div>

          {/* Inline photo on mobile under text */}
          <div className="mt-12 lg:hidden">
            <PortraitCard t={t} />
          </div>
        </div>

        {/* Right — portrait + signature card */}
        <div className="hidden lg:block lg:col-span-5">
          <PortraitCard t={t} />
        </div>
      </div>
    </section>
  );
}

function PortraitCard({ t }) {
  return (
    <div className="relative">
      <div className="absolute -top-6 -left-6 font-mono text-[10px] tracking-[0.2em] uppercase text-mist hidden md:block">
        ◇ exhibit_a
      </div>

      <div className="relative bg-sand border border-rule rounded-[2px] p-4 sm:p-6 hat-rotate">
        <div className="aspect-[4/5] overflow-hidden rounded-sm bg-sand">
          <img
            data-testid="hero-portrait"
            src={PHOTO}
            alt={t.hero.photo_alt}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="mt-4 flex items-baseline justify-between gap-4">
          <div>
            <div className="font-serif text-2xl leading-tight">Nickolass Jensen</div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-mist mt-1">
              founder · nettpilot · w/ hat
            </div>
          </div>
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-ember">
            est. 2003
          </div>
        </div>
      </div>

      {/* Floating cite badge */}
      <div className="absolute -bottom-5 -right-3 sm:-right-6 bg-paper border border-ink px-4 py-2 rounded-full font-mono text-[10px] tracking-[0.2em] uppercase shadow-[2px_2px_0_0_#1A1A1A]">
        ★ cited by chatgpt
      </div>
    </div>
  );
}
